import React, { useEffect, useState, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  Button,
  Upload,
  message,
  Space,
  Typography,
  Spin,
  Alert,
  Tooltip,
} from 'antd';
import {
  SaveOutlined,
  UploadOutlined,
  DeleteOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { brandingService } from '../services/brandingService';

const { Title, Text } = Typography;

// Resolve a DB-stored logo path (e.g. "/uploads/branding/companyLight.png")
// into a fully-qualified URL the browser can fetch. The admin client uses a
// REACT_APP_API_URL like http://host:5003/api — strip the trailing /api so
// /uploads/* lands at the static handler we mounted on the server.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';
const ORIGIN = API_BASE.replace(/\/api\/?$/, '');
const toFullUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Each variant has a label + the surface it'll appear on, so the operator
// understands why we ask for a light vs. dark version.
const LOGO_VARIANTS = [
  {
    key: 'companyLight',
    title: 'Company logo · Light',
    hint: 'Used in the deep-blue invoice header. Pick a logo with a transparent background and light/white fills so it stays legible on dark surfaces.',
    onDark: true,
  },
  {
    key: 'companyDark',
    title: 'Company logo · Dark',
    hint: 'Reserved for any future light surfaces (e.g. a printed cover sheet). Use the regular full-colour logo here.',
    onDark: false,
  },
  {
    key: 'productLight',
    title: 'Product logo · Light',
    hint: 'Sits on the deep-blue invoice footer band. Use a light/white version of the product mark.',
    onDark: true,
  },
  {
    key: 'productDark',
    title: 'Product logo · Dark',
    hint: 'Backup variant for light surfaces. Use the standard product logo here.',
    onDark: false,
  },
];

const BrandingTab = () => {
  const [form] = Form.useForm();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});

  const fetchBranding = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brandingService.getBranding();
      setBranding(res.data);
      form.setFieldsValue(res.data?.issuer || {});
    } catch (err) {
      message.error(err.message || 'Failed to load branding');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const handleSaveIssuer = async (values) => {
    setSaving(true);
    try {
      const res = await brandingService.updateIssuer(values);
      setBranding(res.data);
      message.success('Branding updated');
    } catch (err) {
      message.error(err.message || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (variant, file) => {
    setUploading((u) => ({ ...u, [variant]: true }));
    try {
      const res = await brandingService.uploadLogo(variant, file);
      setBranding(res.data?.branding);
      message.success('Logo uploaded');
    } catch (err) {
      message.error(err.message || 'Failed to upload logo');
    } finally {
      setUploading((u) => ({ ...u, [variant]: false }));
    }
    // Returning false tells antd Upload not to do its own POST — we own the
    // request via the service.
    return false;
  };

  const handleRemove = async (variant) => {
    setUploading((u) => ({ ...u, [variant]: true }));
    try {
      const res = await brandingService.removeLogo(variant);
      setBranding(res.data);
      message.success('Logo removed');
    } catch (err) {
      message.error(err.message || 'Failed to remove logo');
    } finally {
      setUploading((u) => ({ ...u, [variant]: false }));
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Card title="Issuer details (Bill From card)" bordered={false}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Saved here, applied to every newly generated invoice — no server restart needed."
          />
          <Form
            layout="vertical"
            form={form}
            onFinish={handleSaveIssuer}
            initialValues={branding?.issuer}
          >
            <Form.Item
              name="companyName"
              label="Company name"
              rules={[{ required: true, message: 'Company name is required' }]}
            >
              <Input placeholder="Qodequay Technologies Pvt. Ltd." />
            </Form.Item>
            <Form.Item name="address" label="Address line">
              <Input placeholder="Office address" />
            </Form.Item>
            <Row gutter={12}>
              <Col span={10}>
                <Form.Item name="city" label="City">
                  <Input placeholder="Pune" />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item name="state" label="State">
                  <Input placeholder="Maharashtra" />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name="zipCode" label="ZIP">
                  <Input placeholder="411001" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ type: 'email', message: 'Enter a valid email' }]}
                >
                  <Input placeholder="billing@qodequay.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="phone" label="Phone">
                  <Input placeholder="+91-XXXXXXXXXX" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="taxId" label="GSTIN">
              <Input placeholder="27ABCDE1234F1Z5" />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                Save details
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card title="Logo variants" bordered={false}>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="The invoice header and footer use deep-blue bands — upload the LIGHT variants of the company and product logos so they stay legible. Dark variants are kept for any future light-themed surfaces."
          />
          <Row gutter={[12, 16]}>
            {LOGO_VARIANTS.map(({ key, title, hint, onDark }) => {
              const url = toFullUrl(branding?.logos?.[key]);
              const previewBg = onDark ? '#0c1f4d' : '#f8fafc';
              const previewBorder = onDark ? '#1e3a8a' : '#e2e8f0';
              return (
                <Col span={24} key={key}>
                  <Card type="inner" size="small" title={title}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      {hint}
                    </Text>
                    <div
                      style={{
                        background: previewBg,
                        border: `1px solid ${previewBorder}`,
                        borderRadius: 8,
                        height: 90,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 12,
                      }}
                    >
                      {url ? (
                        <img
                          src={url}
                          alt={title}
                          style={{ maxHeight: 64, maxWidth: '70%', objectFit: 'contain' }}
                        />
                      ) : (
                        <Space>
                          <PictureOutlined style={{ color: onDark ? '#bae6fd' : '#94a3b8' }} />
                          <Text style={{ color: onDark ? '#bae6fd' : '#64748b' }}>
                            No logo uploaded — using bundled default
                          </Text>
                        </Space>
                      )}
                    </div>
                    <Space>
                      <Upload
                        beforeUpload={(file) => handleUpload(key, file)}
                        showUploadList={false}
                        accept=".png,.jpg,.jpeg,.webp,.svg"
                      >
                        <Button
                          icon={<UploadOutlined />}
                          loading={uploading[key]}
                          type={url ? 'default' : 'primary'}
                        >
                          {url ? 'Replace' : 'Upload'}
                        </Button>
                      </Upload>
                      {url && (
                        <Tooltip title="Remove (falls back to bundled default)">
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            loading={uploading[key]}
                            onClick={() => handleRemove(key)}
                          >
                            Remove
                          </Button>
                        </Tooltip>
                      )}
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      </Col>
    </Row>
  );
};

export default BrandingTab;
